// 📄 pages/Proyectos/components/GuiaIntegracionCard.tsx
//
// "Guía de Integración Estándar" (2026-08-26, a pedido del usuario) —
// checklist de configuración para ESTE proyecto, resuelto automáticamente
// por (solución, erpTipo) contra el catálogo global (ver
// guiasIntegracionService.ts). Se usa desde SolucionPlaceholder.tsx (SLP/
// Sourcing/Buying/Contracts/CIG) y CommerceHome.tsx (Commerce), en el
// mismo punto donde ya vive <IntegrationSection/>. Si el Admin todavía no
// cargó una guía para esta combinación puntual, no renderiza NADA — no
// tiene sentido mostrar una tarjeta vacía "guía no disponible" en cada
// proyecto que no la tenga.
import { useEffect, useState } from 'react';
import { BookOpenCheck, CheckCircle2, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import { GuiaIntegracion, getGuiaIntegracion, setPasoGuiaCompletado } from '../../../services/guiasIntegracionService.ts';

interface GuiaIntegracionCardProps {
  projectId: string;
  solucion: string;
}

export default function GuiaIntegracionCard({ projectId, solucion }: GuiaIntegracionCardProps) {
  const { project, esCliente } = useProject();
  const [guia, setGuia] = useState<GuiaIntegracion | null>(null);
  const [loading, setLoading] = useState(true);
  const [abierto, setAbierto] = useState(false);
  const [guardandoPasoId, setGuardandoPasoId] = useState<string | null>(null);

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    getGuiaIntegracion(solucion, project?.erpTipo || '')
      .then((g) => { if (!cancelado) setGuia(g); })
      .catch((err) => { console.error('Error cargando la Guía de Integración:', err); if (!cancelado) setGuia(null); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, [solucion, project?.erpTipo]);

  if (loading || !guia || guia.pasos.length === 0) return null;

  // Namespaceado por solución (no por guía) porque el erpTipo del proyecto
  // es único — ver el comentario en projectService.ts sobre guiaIntegracionProgreso.
  const progreso: Record<string, boolean> = project?.guiaIntegracionProgreso?.[solucion] || {};
  const completados = guia.pasos.filter((p) => progreso[p.id]).length;

  // Un cliente no puede escribir NADA en projects/{id} (ver esClienteDeProyecto
  // en firestore.rules) — esto solo evita el intento fallido en la UI, la
  // protección real ya está del lado del servidor.
  const toggle = async (pasoId: string) => {
    if (esCliente || guardandoPasoId) return;
    const hecho = !progreso[pasoId];
    setGuardandoPasoId(pasoId);
    try {
      await setPasoGuiaCompletado(projectId, solucion, pasoId, hecho);
    } catch (err) {
      console.error('Error guardando progreso de la guía:', err);
    } finally {
      setGuardandoPasoId(null);
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        className="w-full flex items-center justify-between gap-4 p-5 text-left hover:bg-white/[0.03] transition-all"
      >
        <div className="flex items-center gap-3 min-w-0">
          <BookOpenCheck className="w-5 h-5 text-indigo-400 flex-shrink-0" />
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-white">Guía de Integración Estándar</h3>
            <p className="text-sm text-gray-400 mt-0.5 truncate">{guia.nombre}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-gray-400">{completados} de {guia.pasos.length}</p>
            <div className="w-24 bg-white/10 h-1.5 rounded-full overflow-hidden mt-1">
              <div className="bg-indigo-500 h-full transition-all duration-300" style={{ width: `${(completados / guia.pasos.length) * 100}%` }} />
            </div>
          </div>
          {abierto ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {abierto && (
        <div className="border-t border-white/10 p-5 space-y-2">
          {guia.descripcion && <p className="text-sm text-gray-400 mb-3">{guia.descripcion}</p>}
          {guia.pasos.map((paso) => {
            const hecho = !!progreso[paso.id];
            return (
              <div
                key={paso.id}
                onClick={() => toggle(paso.id)}
                className={`flex items-start gap-3 p-3 rounded-xl transition-all border border-transparent ${esCliente ? '' : 'cursor-pointer hover:bg-white/5'}`}
              >
                <div className="mt-0.5 text-indigo-400 flex-shrink-0">
                  {hecho ? <CheckCircle2 className="w-5 h-5 fill-indigo-500 text-slate-900" /> : <Circle className="w-5 h-5 text-gray-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${hecho ? 'line-through text-gray-500' : 'text-white'}`}>{paso.titulo}</p>
                  {paso.descripcion && <p className="text-xs text-gray-400 mt-0.5">{paso.descripcion}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
